// src/components/Battle.js
import React, { useState, useEffect } from 'react';
import { playSound, playSelectSound, playBackgroundMusic, stopBackgroundMusic } from '../utils/sound';
import attackSound from '../assets/sounds/attack.mp3';
import magicSound from '../assets/sounds/magic.mp3';
import gameOverSound from '../assets/sounds/game-over.mp3';
import swordAttackSound from '../assets/sounds/sword-attack.wav';
import victorySound from '../assets/sounds/victory.mp3'; // Agrega un sonido de victoria
import bowAttackSound from '../assets/sounds/bow-attack.wav';
import staffAttackSound from '../assets/sounds/staff-attack.wav';
import axeAttackSound from '../assets/sounds/axe-attack.wav';
import pyroSound from '../assets/sounds/pyro.mp3';
import frioSound from '../assets/sounds/frio.wav';
import terremotoSound from '../assets/sounds/terremoto.mp3';
import fenixSound from '../assets/sounds/fenix.wav';


const Battle = ({ character, playerName, onBattleEnd }) => {
  // Arma predeterminada según el personaje
  const initialEquipment = {
    Caballero: 'Espada Básica',
    Arquero: 'Arco Largo',
    Mago: 'Báculo Mágico',
  }[character];

  // Estados del jugador
  const [playerHealth, setPlayerHealth] = useState(
    character === 'Caballero' ? 100 : character === 'Arquero' ? 70 : 50
  );
  const [playerMana, setPlayerMana] = useState(
    character === 'Caballero' ? 50 : character === 'Arquero' ? 70 : 100
  );
  const [equipment, setEquipment] = useState(initialEquipment);
  const [attackPower, setAttackPower] = useState(character === 'Caballero' ? 20 : character === 'Arquero' ? 15 : 10);
  const [magicPower, setMagicPower] = useState(character === 'Mago' ? 20 : character === 'Arquero' ? 15 : 10);

  // Estados de los enemigos
  const [enemies, setEnemies] = useState([
    { name: 'Latido celestial', health: 50, image: require('../assets/monster-heart-phase1.png') },
    { name: 'Corazón del caos', health: 100, image: require('../assets/monster-heart-phase2.png') },
    { name: 'Cupido', health: 200, image: require('../assets/monster-heart-phase3.png') },
  ]);
  const [currentEnemyIndex, setCurrentEnemyIndex] = useState(0);

  // Estados del juego
  const [round, setRound] = useState(1);
  const [playerTurn, setPlayerTurn] = useState(true);
  const [battleLog, setBattleLog] = useState([]);

  // Estados para mostrar opciones
  const [showMagics, setShowMagics] = useState(false);
  const [showWeapons, setShowWeapons] = useState(false);

  // Armas disponibles
  const weapons = {
    'Espada Básica': { attack: 10, magic: 0, sound: swordAttackSound },
    'Arco Largo': { attack: 15, magic: 5, sound: bowAttackSound },
    'Báculo Mágico': { attack: 5, magic: 20, sound: staffAttackSound },
    'Hacha Pesada': { attack: 20, magic: 0, sound: axeAttackSound },
  };

  // Magias disponibles
  const magics = {
    Pyro: { cost: 20, effect: 'Daño de fuego', sound: pyroSound, damage: 30 },
    Frío: { cost: 20, effect: 'Daño de hielo', sound: frioSound, damage: 30 },
    Terremoto: { cost: 30, effect: 'Daño masivo', sound: terremotoSound, damage: 50 },
    'Esencia de Fénix': { cost: 40, effect: 'Regenera salud', sound: fenixSound, heal: 50 },
  };

  // Cambiar de arma
  const changeEquipment = (weapon) => {
    setEquipment(weapon);
    setAttackPower(weapons[weapon].attack);
    setMagicPower(weapons[weapon].magic);
    setBattleLog(prev => [...prev, `Cambiaste tu equipo a: ${weapon}.`]);
    playSound(weapons[weapon].sound);
    setShowWeapons(false);
    setPlayerTurn(false); // Cambiar al turno del enemigo
  };

  // Atacar
  const attack = () => {
    const damage = attackPower + Math.floor(Math.random() * 10);
    const updatedEnemies = [...enemies];
    updatedEnemies[currentEnemyIndex].health = Math.max(0, updatedEnemies[currentEnemyIndex].health - damage);
    setEnemies(updatedEnemies);
    setBattleLog(prev => [...prev, `Atacaste a ${enemies[currentEnemyIndex].name} y le hiciste ${damage} de daño.`]);
    playSound(weapons[equipment].sound);
    setPlayerTurn(false); // Cambiar al turno del enemigo
  };

  // Usar magia
  const castMagic = (magic) => {
    if (playerMana >= magics[magic].cost) {
      let effect = '';
      const updatedEnemies = [...enemies];
      switch (magic) {
        case 'Pyro':
        case 'Frío':
        case 'Terremoto':
          const damage = magics[magic].damage + Math.floor(Math.random() * 20);
          updatedEnemies[currentEnemyIndex].health = Math.max(0, updatedEnemies[currentEnemyIndex].health - damage);
          effect = `Lanzaste ${magic} y le hiciste ${damage} de daño a ${enemies[currentEnemyIndex].name}.`;
          break;
        case 'Esencia de Fénix':
          const heal = magics[magic].heal;
          setPlayerHealth(prev => Math.min(100, prev + heal));
          effect = `Usaste Esencia de Fénix y recuperaste ${heal} de salud.`;
          break;
        default:
          break;
      }
      setPlayerMana(prev => prev - magics[magic].cost);
      setEnemies(updatedEnemies);
      setBattleLog(prev => [...prev, effect]);
      playSound(magics[magic].sound);
      setShowMagics(false);
      setPlayerTurn(false);
    } else {
      setBattleLog(prev => [...prev, 'No tienes suficiente maná para lanzar esta magia.']);
    }
  };

  // Ataque del enemigo
    const enemyAttack = () => {
        const missChance = 0.2; // 20% de probabilidad de fallar
        if (Math.random() < missChance) {
        setBattleLog(prev => [...prev, `${enemies[currentEnemyIndex].name} intentó atacar, pero falló.`]);
        } else {
        const damage = Math.floor(Math.random() * 15) + 5;
        setPlayerHealth(prev => Math.max(0, prev - damage));
        setBattleLog(prev => [...prev, `${enemies[currentEnemyIndex].name} te atacó y te hizo ${damage} de daño.`]);
        playSound(attackSound);
        }
        setPlayerTurn(true); // Cambiar al turno del jugador
    };
  // Lógica del turno del enemigo
  useEffect(() => {
    if (!playerTurn && enemies[currentEnemyIndex].health > 0 && playerHealth > 0) {
      const enemyTurnTimeout = setTimeout(() => {
        enemyAttack();
      }, 1000); // El enemigo ataca después de 1 segundo

      return () => clearTimeout(enemyTurnTimeout); // Limpiar el timeout si el componente se desmonta
    }
  }, [playerTurn, enemies, currentEnemyIndex, playerHealth]);

  // Cambiar de enemigo cuando el actual es derrotado
  useEffect(() => {
    if (enemies[currentEnemyIndex].health <= 0) {
      setBattleLog(prev => [...prev, `¡Has derrotado a ${enemies[currentEnemyIndex].name}!`]);
      setPlayerHealth(prev => Math.min(100, prev + 50)); // Recuperar 50 de salud
      if (currentEnemyIndex < enemies.length - 1) {
        setCurrentEnemyIndex(prev => prev + 1); // Pasar al siguiente enemigo
      } else {
        playSound(victorySound);
        stopBackgroundMusic();
        onBattleEnd('win'); // Notificar que el jugador ganó
      }
    }
  }, [enemies, currentEnemyIndex, onBattleEnd]);

  // Fin de la batalla (jugador pierde)
  useEffect(() => {
    if (playerHealth <= 0) {
      playSound(gameOverSound);
      stopBackgroundMusic();
      onBattleEnd('lose'); // Notificar que el jugador perdió
    }
  }, [playerHealth, onBattleEnd]);

  // Iniciar música de fondo al comenzar la batalla
  useEffect(() => {
    playBackgroundMusic();
    return () => stopBackgroundMusic(); // Detener la música al desmontar el componente
  }, []);

  return (
    <div className="battle-container">
      <h2>Ronda {round}</h2>
      <div className="characters">
        <div className="player">
          <img src={require(`../assets/${character.toLowerCase()}.png`)} alt={character} />
          <div className="stats">
            <p>Nombre: {playerName}</p>
            <p>Salud: {playerHealth}</p>
            <p>Maná: {playerMana}</p>
            <p>Equipamiento: {equipment}</p>
          </div>
        </div>
        <div className="enemy">
          <img src={enemies[currentEnemyIndex].image} alt="Enemigo" />
          <div className="stats">
            <p>Nombre: {enemies[currentEnemyIndex].name}</p>
            <p>Salud: {enemies[currentEnemyIndex].health}</p>
          </div>
        </div>
      </div>
      <div className="actions">
        <h3>Opciones</h3>
        {playerTurn ? (
          <>
            {!showMagics && !showWeapons && (
              <>
                <button onClick={() => { attack(); playSelectSound(); }}>Atacar</button>
                <button onClick={() => { setShowMagics(true); playSelectSound(); }}>Magia</button>
                <button onClick={() => { setShowWeapons(true); playSelectSound(); }}>Cambiar Equipamiento</button>
              </>
            )}
            {showMagics && (
              <>
                <button onClick={() => { castMagic('Pyro'); playSelectSound(); }}>Pyro</button>
                <button onClick={() => { castMagic('Frío'); playSelectSound(); }}>Frío</button>
                <button onClick={() => { castMagic('Terremoto'); playSelectSound(); }}>Terremoto</button>
                <button onClick={() => { castMagic('Esencia de Fénix'); playSelectSound(); }}>Esencia de Fénix</button>
                <button onClick={() => { setShowMagics(false); playSelectSound(); }}>Volver</button>
              </>
            )}
            {showWeapons && (
              <>
                {Object.keys(weapons).map((weapon) => (
                  <button key={weapon} onClick={() => { changeEquipment(weapon); playSelectSound(); }}>
                    {weapon}
                  </button>
                ))}
                <button onClick={() => { setShowWeapons(false); playSelectSound(); }}>Volver</button>
              </>
            )}
          </>
        ) : (
          <p>Esperando el turno del enemigo...</p>
        )}
      </div>
      <div className="battle-log">
        <h3>Registro de Batalla</h3>
        <ul>
          {battleLog.map((log, index) => (
            <li key={index}>{log}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Battle;